import type { Session, User } from "@supabase/supabase-js";
import { create } from "zustand";
import { getSupabase, isSupabaseConfigured } from "../supabase/client";
import type { AccessRole } from "../supabase/api";
import { isCurrentUserAdmin } from "../supabase/admin";
import { useI18n } from "../i18n/store";
import { isLocale } from "../i18n/locales";
import { identify, registerSuperProperties, resetAnalyticsIdentity } from "../analytics";
import { firstTouchProperties } from "../first-touch";
import { useSubscriptionEditingBlocked } from "../billing/subscription-access";
import { LANDING_PATH } from "../landing-gate";
import { finishOAuthCallback, hasOAuthCallback } from "./oauth-analytics";

const ANON_NAME_KEY = "twelve-volt:anon-name";

export function getAnonName(): string {
  return localStorage.getItem(ANON_NAME_KEY) ?? "";
}
export function setAnonName(name: string): void {
  localStorage.setItem(ANON_NAME_KEY, name);
}

interface AuthState {
  ready: boolean;
  session: Session | null;
  user: User | null;
  currentProjectId: string | null;
  /** When loaded via a share link, the token used. null for owner loads. */
  currentShareToken: string | null;
  currentRole: AccessRole | null;
  /** Last cloud-saved updated_at, for staleness detection. */
  currentUpdatedAt: string | null;
  isPublicView: boolean;
  currentOwnerId: string | null;
  /** Public slug when published. Exported artifacts link off this, never off
   *  the share token. */
  currentPublicSlug: string | null;
  /** Drives whether the OG preview image is regenerated after edits. */
  hasShares: boolean;
  isAdmin: boolean;
  /** A pending admin lookup must not recruit an owner into experiments. */
  adminReady: boolean;

  setSession(session: Session | null): void;
  setIsAdmin(isAdmin: boolean): void;
  setCurrentProject(args: {
    id: string | null;
    role: AccessRole | null;
    token: string | null;
    updatedAt: string | null;
    isPublicView?: boolean;
    ownerId?: string | null;
    publicSlug?: string | null;
  }): void;
  setHasShares(hasShares: boolean): void;
  setPublicSlug(slug: string | null): void;
  clearCurrentProject(): void;
  signOut(): Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ready: false,
  session: null,
  user: null,
  currentProjectId: null,
  currentShareToken: null,
  currentRole: null,
  currentUpdatedAt: null,
  isPublicView: false,
  currentOwnerId: null,
  currentPublicSlug: null,
  hasShares: false,
  isAdmin: false,
  adminReady: false,

  setSession: (session) => set({
    session, user: session?.user ?? null, ready: true,
    ...(session?.user.id !== get().user?.id ? { isAdmin: false, adminReady: !session } : {}),
  }),
  setIsAdmin: (isAdmin) => set({ isAdmin, adminReady: true }),
  setCurrentProject: ({ id, role, token, updatedAt, isPublicView, ownerId, publicSlug }) => {
    registerSuperProperties({
      view: isPublicView ? "public_project" : id ? "cloud_project" : "local_canvas",
      project_role: role,
      via_share_token: Boolean(token),
    });
    set({
      currentProjectId: id,
      currentRole: role,
      currentShareToken: token,
      currentUpdatedAt: updatedAt,
      isPublicView: isPublicView ?? false,
      currentOwnerId: ownerId ?? null,
      currentPublicSlug: publicSlug ?? null,
      hasShares: false,
    });
  },
  setHasShares: (hasShares) => set({ hasShares }),
  setPublicSlug: (slug) => set({ currentPublicSlug: slug }),
  clearCurrentProject: () => {
    registerSuperProperties({
      view: "local_canvas",
      project_role: null,
      via_share_token: false,
    });
    set({
      currentProjectId: null,
      currentRole: null,
      currentShareToken: null,
      currentUpdatedAt: null,
      isPublicView: false,
      currentOwnerId: null,
      currentPublicSlug: null,
      hasShares: false,
    });
  },
  signOut: async () => {
    if (!isSupabaseConfigured) return;
    await getSupabase().auth.signOut();
    // `/` resolves straight back to the canvas when a local schematic exists.
    window.location.replace(LANDING_PATH);
  },
}));

function reconcileLocaleFromSession(session: Session | null): void {
  const i18n = useI18n.getState();
  if (i18n.profileReconciled) return;
  const profileLocale = session?.user?.user_metadata?.locale;
  if (isLocale(profileLocale)) {
    i18n.applyProfileLocale(profileLocale);
  }
  i18n.markProfileReconciled();
}

let lastAdminCheckUserId: string | null = null;
function reconcileAdminStatus(session: Session | null): void {
  const userId = session?.user?.id ?? null;
  if (userId === lastAdminCheckUserId) return;
  lastAdminCheckUserId = userId;
  if (!userId) {
    useAuthStore.getState().setIsAdmin(false);
    return;
  }
  void isCurrentUserAdmin().then((isAdmin) => {
    // A sign-out may have raced ahead of this resolution.
    if (lastAdminCheckUserId === userId && isAdmin !== null) {
      useAuthStore.getState().setIsAdmin(isAdmin);
    }
  }).catch(() => { /* Unknown admin status remains ineligible for experiments. */ });
}

let lastIdentifiedUserId: string | null = null;

/** Idempotent, so a token refresh is not a second identity event. */
function identifySession(session: Session): void {
  const user = session.user;
  if (user.id === lastIdentifiedUserId) return;
  const provider = user.app_metadata?.provider ?? "email";
  const locale = useI18n.getState().locale;
  identify(user.id, {
    email: user.email,
    created_at: user.created_at,
    provider,
    locale,
    name: user.user_metadata?.full_name ?? user.user_metadata?.name,
    // Person properties, not super: revenue cohorts on the person, not on events.
    ...firstTouchProperties(),
  });
  registerSuperProperties({ signed_in: true, auth_provider: provider, locale });
  lastIdentifiedUserId = user.id;
}

/**
 * `posthog.reset()` mints a fresh anonymous distinct id, so only a session that
 * went from present to absent may call this — one that merely failed to find a
 * session would turn a returning reader into a second visitor.
 */
function forgetIdentity(): void {
  if (lastIdentifiedUserId === null) return;
  resetAnalyticsIdentity();
  registerSuperProperties({ signed_in: false, locale: useI18n.getState().locale });
  lastIdentifiedUserId = null;
}

function reconcileAnalyticsIdentity(session: Session | null): void {
  if (session?.user) identifySession(session);
  else forgetIdentity();
}

async function recordOAuthCallback(session: Session | null, sessionError?: unknown): Promise<void> {
  if (!hasOAuthCallback()) return;
  try {
    // getSession omits redirect errors; initialize returns the cached outcome.
    // Keep this outside onAuthStateChange to avoid re-entering the auth lock.
    const { error } = await getSupabase().auth.initialize();
    finishOAuthCallback(session, error ?? sessionError);
  } catch (error) {
    finishOAuthCallback(null, error);
  }
}

/**
 * For surfaces that never boot auth, such as the SKU pages.
 *
 * Identify-only, deliberately: a null session here means "could not confirm",
 * not "signed out", so routing it through the sign-out path would reset a
 * reader's identity on an ordinary page read.
 */
export async function identifyPersistedSession(): Promise<void> {
  if (!isSupabaseConfigured) return;
  try {
    const { data, error } = await getSupabase().auth.getSession();
    if (data.session?.user) identifySession(data.session);
    await recordOAuthCallback(data.session, error);
  } catch (error) {
    finishOAuthCallback(null, error);
    throw error;
  }
}

/** Subscribe to auth changes; call once at app boot. */
export function bootAuth(): () => void {
  if (!isSupabaseConfigured) {
    useAuthStore.setState({ ready: true });
    useI18n.getState().markProfileReconciled();
    return () => {};
  }
  const sb = getSupabase();
  sb.auth.getSession().then(async ({ data, error }) => {
    useAuthStore.getState().setSession(data.session);
    reconcileLocaleFromSession(data.session);
    reconcileAnalyticsIdentity(data.session);
    reconcileAdminStatus(data.session);
    await recordOAuthCallback(data.session, error);
  }).catch((error: unknown) => {
    finishOAuthCallback(null, error);
  });
  const { data } = sb.auth.onAuthStateChange((_event, session) => {
    useAuthStore.getState().setSession(session);
    reconcileLocaleFromSession(session);
    reconcileAnalyticsIdentity(session);
    reconcileAdminStatus(session);
  });
  return () => data.subscription.unsubscribe();
}

export function canEdit(role: AccessRole | null): boolean {
  return role === "owner" || role === "editor";
}

/** Sharing roles and the subscription offer both constrain editing. */
export function useIsViewOnly(): boolean {
  const roleReadOnly = useAuthStore((s) => s.currentRole !== null && !canEdit(s.currentRole));
  const subscriptionReadOnly = useSubscriptionEditingBlocked();
  return roleReadOnly || subscriptionReadOnly;
}

export function canComment(role: AccessRole | null): boolean {
  return role === "owner" || role === "editor" || role === "commenter";
}

/** RLS enforces the same gate server-side. */
export function useCanComment(): boolean {
  return useAuthStore((s) => {
    if (canComment(s.currentRole)) return true;
    return s.isPublicView && Boolean(s.session);
  });
}

/** RLS enforces the same gate server-side. */
export function useCanResolve(): boolean {
  return useAuthStore((s) => {
    if (s.currentRole === "owner" || s.currentRole === "editor") return true;
    if (
      s.isPublicView &&
      s.currentOwnerId &&
      s.session?.user?.id === s.currentOwnerId
    ) {
      return true;
    }
    return false;
  });
}

/** Owner only, not share-link editors. The server-side RPC enforces the same. */
export function useCanPin(): boolean {
  return useAuthStore((s) => {
    if (s.currentRole === "owner") return true;
    if (
      s.isPublicView &&
      s.currentOwnerId &&
      s.session?.user?.id === s.currentOwnerId
    ) {
      return true;
    }
    return false;
  });
}
